import {useMemo} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {ColumnDef} from '@tanstack/react-table';
import {useTranslation} from 'react-i18next';
import {GuestLanguageFlag, useStayDates} from '../../../common/guestTable/guestTableCells';
// Straight from the modules, not the folder index: the index also pulls in the toolbar's search,
// whose debouncer package the Jest setup cannot resolve, and this table has no toolbar.
import ServerTable from '../../../common/serverTable/ServerTable';
import {ServerTableProvider} from '../../../common/serverTable/ServerTableContext';
import {ServerTableFrame} from '../../../common/serverTable/ServerTableFrame';
import {useServerTable} from '../../../common/serverTable/useServerTable';
import {detailTitleOf, ListRow, matchesSearch, sortRows, StepList} from './previewLists';

/** The optional columns a dialog shows: when any of its rows has the value. */
export interface PeopleColumns {
    stay: boolean;
    room: boolean;
    language: boolean;
}

type KeyedRow = ListRow & {key: string};

const PAGE_SIZE = 10;

interface StepPeopleTableProps {
    list: StepList;
    columns: PeopleColumns;
    /** Already lower-cased. */
    query: string;
}

// The rows are already loaded, so the ServerTable's "server" is this list.
const StepPeopleTable = ({list, columns, query}: StepPeopleTableProps) => {
    const {t, i18n} = useTranslation();
    const detailTitle = detailTitleOf(list.rows, t);
    const language = i18n?.resolvedLanguage || i18n?.language;
    const collator = useMemo(() => new Intl.Collator(language, {sensitivity: 'base'}), [language]);
    const stayDates = useStayDates();

    const rows = useMemo<KeyedRow[]>(
        () => list.rows
            .filter(row => matchesSearch(row, query))
            .map((row, index) => ({...row, key: `${row.name}-${index}`})),
        [list.rows, query]
    );

    const tableColumns = useMemo<ColumnDef<KeyedRow>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: row => row.name,
            cell: ({row}) => (
                <Typography variant={'body1'} noWrap>
                    {row.original.name}
                    {row.original.times && (
                        <Box component={'span'} sx={{ml: 0.75, color: 'text.secondary'}}>×{row.original.times}</Box>
                    )}
                </Typography>
            ),
            size: 200,
        },
        {
            id: 'detail',
            header: detailTitle,
            accessorFn: row => row.detail,
            cell: ({row}) => (
                // Wraps: a reason names the unmet rule, and a cut-off one hides why the guest stopped.
                <Typography variant={'body1'} color={row.original.problem ? 'error.main' : 'text.secondary'}
                            sx={{whiteSpace: 'normal', overflowWrap: 'anywhere'}}>
                    {row.original.detail || '—'}
                </Typography>
            ),
            size: 260,
            meta: {grow: true},
        },
        ...(columns.stay ? [{
            id: 'stay',
            header: t('Stay dates'),
            accessorFn: (row: KeyedRow) => row.arrival,
            cell: ({row}: {row: {original: KeyedRow}}) => (
                <Typography variant={'body1'} noWrap>
                    {stayDates(row.original.arrival, row.original.departure) || '—'}
                </Typography>
            ),
            size: 150,
        } as ColumnDef<KeyedRow>] : []),
        ...(columns.room ? [{
            id: 'room',
            header: t('Room'),
            accessorFn: (row: KeyedRow) => row.room,
            cell: ({row}: {row: {original: KeyedRow}}) => <Typography variant={'body1'} noWrap>{row.original.room || '—'}</Typography>,
            size: 100,
        } as ColumnDef<KeyedRow>] : []),
        ...(columns.language ? [{
            id: 'language',
            header: t('Language'),
            accessorFn: (row: KeyedRow) => row.language,
            cell: ({row}: {row: {original: KeyedRow}}) => <GuestLanguageFlag language={row.original.language}/>,
            size: 110,
        } as ColumnDef<KeyedRow>] : []),
    ], [t, detailTitle, columns.stay, columns.room, columns.language, stayDates]);

    const controller = useServerTable<KeyedRow>({
        tableId: `automation-step-people-${list.title}`,
        presentation: 'embedded',
        columns: tableColumns,
        getRowId: row => row.key,
        defaultPageSize: PAGE_SIZE,
        initialSorting: [{id: 'name', desc: false}],
        queryDependencies: [rows],
        queryFn: async ({pagination, sorting}) => {
            const sorted = sortRows(rows, sorting?.field ?? 'name', sorting?.direction === 'DESC', collator);
            const start = pagination.pageIndex * pagination.pageSize;
            return {content: sorted.slice(start, start + pagination.pageSize), totalElements: sorted.length};
        },
    });

    if (!rows.length) return null;

    return (
        <Box>
            <Stack direction={'row'} alignItems={'baseline'} gap={1} sx={{pb: 1}}>
                <Typography variant={'subtitle1'} fontWeight={600}>{list.title}</Typography>
                <Typography variant={'body1'} color={'text.secondary'}>{list.count}</Typography>
            </Stack>
            {list.reason && <Alert severity={'error'} sx={{mb: 1}}>{list.reason}</Alert>}
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );
};

export default StepPeopleTable;
